import React, { useEffect, useMemo, useState } from 'react';
import { Table, Tag, Button, Space, Spin, Alert, Typography, Checkbox } from 'antd';
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getPanelTitularesSinVincular,
  type TitularSinVincular,
  type TitularSinVincularCategoria,
} from '../../api/vinculacionApi';
import { FctIcono, FctGlifoAtencion, claseFilaAtencion, type FctNivelAtencion } from '../../fct/react';
import { exportCsv } from './helpers';

const { Paragraph, Text } = Typography;

// Categorías (tipos de caso: etiqueta neutra + nivel de atención para el filete de fila) — orden de presentación
const CATS: { key: TitularSinVincularCategoria; label: string; nivel: FctNivelAtencion }[] = [
  { key: 'DUPLICADO_GS', label: 'Duplicado Gescom', nivel: 'atencion' },
  { key: 'VINCULABLE', label: 'Vinculable (titular CU2 coincide, sin vincular)', nivel: 'normal' },
  { key: 'TITULAR_DISTINTO', label: 'Distinto cotitular (un titular CU2 ya consta como titular de esta farmacia en GS)', nivel: 'urgente' },
  { key: 'TITULAR_CU2_NO_EN_GS', label: 'Distinto titular (el titular CU2 no consta como titular de esta farmacia en GS)', nivel: 'atencion' },
  { key: 'CU2_SIN_TITULAR', label: 'Farmacia sin titular en CU2', nivel: 'normal' },
  { key: 'SIN_FARMACIA_CU2', label: 'Farmacia no presente en CU2', nivel: 'normal' },
];
const CAT_META = Object.fromEntries(CATS.map((c) => [c.key, c])) as Record<
  TitularSinVincularCategoria,
  { key: TitularSinVincularCategoria; label: string; nivel: FctNivelAtencion }
>;

/** Marcas de cotitularidad en una tinta: check = titular de esta misma farmacia en GS; glifo de atención = titular de otra farmacia. */
const MarcaTitularEsta: React.FC = () => (
  <span title="Titular de esta misma farmacia en Gescom" style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>
    <FctIcono nombre="check" style={{ width: 14, height: 14 }} />
  </span>
);
const MarcaTitularOtra: React.FC = () => (
  <span title="Titular de otra farmacia en Gescom" style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>
    <FctGlifoAtencion nivel="atencion" />
  </span>
);

const VinculacionTitularesSinVincularTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rows, setRows] = useState<TitularSinVincular[]>([]);
  const [activas, setActivas] = useState<Set<string>>(new Set(CATS.map((c) => c.key)));

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      setRows(await getPanelTitularesSinVincular());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    CATS.forEach((cat) => (c[cat.key] = rows.filter((r) => r.categoria === cat.key).length));
    return c;
  }, [rows]);

  const data = rows.filter((r) => activas.has(r.categoria));

  const toggle = (key: string) =>
    setActivas((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          Clasificando titulares (análisis de nombres T06)…
        </Paragraph>
      </div>
    );
  }
  if (error) {
    return <Alert type="error" showIcon message="No se pudieron cargar los titulares sin vincular" action={<Button onClick={load}>Reintentar</Button>} />;
  }

  // Celda de id_colegiado_gs con cruces de cotitularidad
  const idColGsCell = (r: TitularSinVincular) =>
    r.titularesCu2
      .filter((t) => t.idColegiadoGs != null)
      .map((t, i) => (
        <React.Fragment key={`${t.idColegiadoGs}-${i}`}>
          {i > 0 && ', '}
          {t.titGsEsta && <MarcaTitularEsta />}
          {t.titGsOtra && <MarcaTitularOtra />}
          {t.idColegiadoGs}
        </React.Fragment>
      ));

  const columns: ColumnsType<TitularSinVincular> = [
    { title: 'Tipo de caso', key: 'cat', render: (_, r) => <Tag>{CAT_META[r.categoria].label}</Tag> },
    { title: 'ID', dataIndex: 'id', className: 'fct-num', align: 'right' },
    { title: 'Titular Gescom', key: 'titGs', render: (_, r) => r.titularGs || '(sin nombre)' },
    {
      title: 'Farmacia (Gescom)',
      key: 'farGs',
      render: (_, r) => r.farmaciaGs || (r.farmaciaIdGs ? `Farmacia ${r.farmaciaIdGs}` : null),
    },
    { title: 'Dirección (CU2)', dataIndex: 'direccionCu2' },
    { title: 'Localidad (CU2)', dataIndex: 'localidadCu2' },
    { title: 'Nombre farmacia (CU2)', dataIndex: 'nombreFarmaciaCu2' },
    { title: 'id_farmacia (CU2)', dataIndex: 'idFarmaciaCu2', className: 'fct-num', align: 'right' },
    { title: 'Titular en CU2', key: 'titCu2', render: (_, r) => r.titularesCu2.map((t) => t.nombre).filter(Boolean).join(', ') },
    { title: 'id_colegiado (titular CU2)', key: 'idColCu2', className: 'fct-num', align: 'right', render: (_, r) => r.titularesCu2.map((t) => t.idColegiado).filter((x) => x != null).join(', ') },
    { title: 'id_colegiado_gs (titular CU2)', key: 'idColGsCu2', className: 'fct-mono', render: (_, r) => idColGsCell(r) },
  ];

  const doExport = () =>
    exportCsv(
      'titulares_gescom_sin_vincular.csv',
      ['Tipo', 'ID', 'Titular_GescomAQ', 'Farmacia_GescomAQ', 'Direccion_CU2', 'Localidad_CU2', 'Nombre_farmacia_CU2', 'id_farmacia_CU2', 'Titular_CU2', 'id_colegiado_titular_CU2', 'id_colegiado_gs_titular_CU2'],
      data.map((r) => [
        CAT_META[r.categoria].label,
        r.id,
        r.titularGs ?? '',
        r.farmaciaGs ?? '',
        r.direccionCu2 ?? '',
        r.localidadCu2 ?? '',
        r.nombreFarmaciaCu2 ?? '',
        r.idFarmaciaCu2 ?? '',
        r.titularesCu2.map((t) => t.nombre ?? '').join(', '),
        r.titularesCu2.map((t) => t.idColegiado).filter((x) => x != null).join(', '),
        r.titularesCu2.map((t) => t.idColegiadoGs).filter((x) => x != null).join(', '),
      ]),
    );

  return (
    <div>
      <Space wrap style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Space wrap>
          <Text type="secondary" style={{ fontWeight: 600 }}>Tipos:</Text>
          {CATS.map((c) => (
            <Checkbox key={c.key} checked={activas.has(c.key)} onChange={() => toggle(c.key)}>
              <Tag style={{ marginInlineEnd: 4 }}>{c.label}</Tag>
              <span className="fct-mono">{counts[c.key]}</span>
            </Checkbox>
          ))}
        </Space>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}><span className="fct-mono">{data.length}</span> de <span className="fct-mono">{rows.length}</span> visibles</Text>
          <Button size="small" icon={<DownloadOutlined />} onClick={doExport}>Exportar CSV</Button>
          <Button size="small" icon={<ReloadOutlined />} onClick={load}>Actualizar</Button>
        </Space>
      </Space>
      <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 4 }}>
        Titulares activos en Gescom sin correspondencia vinculada en CU2, clasificados por tipo de caso (lógica T06).
      </Paragraph>
      <Paragraph type="secondary" style={{ fontSize: 12 }}>
        En «id_colegiado_gs (titular CU2)»: <MarcaTitularEsta /> titular de esta misma farmacia en
        Gescom · <MarcaTitularOtra /> titular de otra farmacia en Gescom.
      </Paragraph>
      <Table
        rowKey={(_, i) => String(i)}
        size="small"
        columns={columns}
        dataSource={data}
        scroll={{ x: true }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        rowClassName={(r) => claseFilaAtencion(CAT_META[r.categoria].nivel)}
      />
    </div>
  );
};

export default VinculacionTitularesSinVincularTab;
